import { useMemo, useState, type ReactNode } from "react";
import { Loader2, Plus } from "lucide-react";
import { dentistStatusBucket, type DentistStatusBucket } from "./dentist-status";
import { DetalheDoPedidoDaLista } from "./DetalheDoPedidoDaLista";
import { usePedidosDaLista } from "./use-pedidos-da-lista";
import { ProsthesisOrdersListView } from "./ProsthesisOrdersListView";
import { ProsthesisOrdersCardView } from "./ProsthesisOrdersCardView";
import { ProsthesisOrdersMobileList } from "./ProsthesisOrdersMobileList";
import { BarraDePedidosCelular } from "./BarraDePedidosCelular";
import { BarraDePedidosDesktop } from "./BarraDePedidosDesktop";
import { useIsMobile } from "@/lib/use-media-query";
import { makeOrderComparator, type OrderSortField, type SortDirection } from "./prosthesis-orders-sort";
import type { ProsthesisOrderRow } from "./types";

type ViewMode = "card" | "list";
const LS_VIEW_MODE = "bookdental.protese.viewMode";

function loadViewMode(): ViewMode {
  if (typeof window === "undefined") return "list";
  return window.localStorage.getItem(LS_VIEW_MODE) === "card" ? "card" : "list";
}

const EMPTY_COUNTS: Record<DentistStatusBucket, number> = {
  AGUARDANDO_CONFIRMACAO: 0,
  RECUSADO: 0,
  ACEITO: 0,
  EM_PRODUCAO: 0,
  ENTREGUE: 0,
  CANCELADO: 0,
};

export function ProsthesisOrdersTable({
  onRequest,
  onEditar,
  lente,
  deTodasAsOrganizacoes = false,
}: {
  onRequest: () => void;
  onEditar?: (order: ProsthesisOrderRow) => void;
  /** Botão Todos / Por profissional — no celular mora ao lado da busca. */
  lente?: ReactNode;
  /** Acesso pessoal de quem está em outras organizações: os pedidos de todas, com filtro. */
  deTodasAsOrganizacoes?: boolean;
}) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  // Lista em vez de valor único: o celular passou a permitir escolher vários status de uma vez.
  // Vazia = todos. Os chips do desktop continuam trocando por um só, como sempre fizeram.
  const [statusFilters, setStatusFilters] = useState<DentistStatusBucket[]>([]);
  const alternarStatus = (s: DentistStatusBucket) =>
    setStatusFilters((atuais) =>
      atuais.includes(s) ? atuais.filter((x) => x !== s) : [...atuais, s],
    );
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  // Padrão: mais recentes primeiro — o que a lista já mostrava sem ordenação escolhida.
  const [sortField, setSortField] = useState<OrderSortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const changeViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    window.localStorage.setItem(LS_VIEW_MODE, mode);
  };

  const listQuery = usePedidosDaLista(deTodasAsOrganizacoes, search);
  const allOrders = listQuery.orders;

  const countByBucket = useMemo(() => {
    const counts = { ...EMPTY_COUNTS };
    for (const o of allOrders) counts[dentistStatusBucket(o.status)]++;
    return counts;
  }, [allOrders]);

  const filteredOrders = useMemo(
    () =>
      statusFilters.length === 0
        ? allOrders
        : allOrders.filter((o) => statusFilters.includes(dentistStatusBucket(o.status))),
    [allOrders, statusFilters],
  );

  const comparator = useMemo(() => makeOrderComparator(sortField, sortDirection), [sortField, sortDirection]);
  // Lista e cartões seguem a mesma ordenação ("Ordenar por" ou o título da coluna).
  const listOrders = useMemo(() => [...filteredOrders].sort(comparator), [filteredOrders, comparator]);


  /** Clica na coluna: se já é o campo ativo, inverte a direção; senão passa a ordenar por ela (crescente). */
  function handleSort(field: OrderSortField) {
    if (field === sortField) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  const ordenar = (campo: OrderSortField, direcao: SortDirection) => {
    setSortField(campo);
    setSortDirection(direcao);
  };

  return (
    <div>
      {isMobile ? (
        <BarraDePedidosCelular
          busca={search}
          onBusca={setSearch}
          lente={lente}
          status={{
            selecionados: statusFilters,
            contagem: countByBucket,
            total: allOrders.length,
            onAlternar: alternarStatus,
            onLimpar: () => setStatusFilters([]),
          }}
          ordem={{ campo: sortField, direcao: sortDirection, onChange: ordenar }}
          visualizacao={viewMode}
          onVisualizacao={changeViewMode}
          carregando={listQuery.isFetching}
          extra={listQuery.filtroEl}
        />
      ) : (
        <BarraDePedidosDesktop
          busca={search}
          onBusca={setSearch}
          status={{
            selecionados: statusFilters,
            contagem: countByBucket,
            total: allOrders.length,
            onLimpar: () => setStatusFilters([]),
            onSomente: (st) => setStatusFilters([st]),
          }}
          ordem={{ campo: sortField, direcao: sortDirection, onChange: ordenar }}
          visualizacao={viewMode}
          onVisualizacao={changeViewMode}
          carregando={listQuery.isFetching}
          extra={listQuery.filtroEl}
        />
      )}

      {listQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-zinc-200 bg-white px-4 py-10 text-center sm:py-16 dark:border-border dark:bg-card">
          <p className="text-sm font-medium text-zinc-900 dark:text-foreground">
            {search || statusFilters.length > 0 ? "Nenhum pedido encontrado" : "Nenhum pedido ainda"}
          </p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            Monte o primeiro pedido de prótese.
          </p>
          <button
            type="button"
            onClick={onRequest}
            className="mt-4 inline-flex h-11 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 sm:h-9"
          >
            <Plus className="h-4 w-4" />
            Novo pedido
          </button>
        </div>
      ) : viewMode === "card" ? (
        <ProsthesisOrdersCardView orders={listOrders} onOpen={setOpenOrderId} />
      ) : (
        <>
          {/* Celular tem apresentação própria; a tabela de sete colunas continua idêntica no
              desktop e no tablet, sob `hidden md:block`. */}
          <div className="md:hidden">
            <ProsthesisOrdersMobileList orders={listOrders} onOpen={setOpenOrderId} />
          </div>
          <div className="hidden md:block">
            <ProsthesisOrdersListView
              orders={listOrders}
              onOpen={setOpenOrderId}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </div>
        </>
      )}

      <DetalheDoPedidoDaLista
        order={allOrders.find((o) => o.id === openOrderId) ?? (openOrderId ? ({ id: openOrderId } as ProsthesisOrderRow) : null)}
        onClose={() => setOpenOrderId(null)}
        onEditar={
          onEditar &&
          ((order) => {
            setOpenOrderId(null);
            onEditar(order);
          })
        }
      />
    </div>
  );
}
